<script setup lang="ts">
import { computed } from 'vue'

interface Props {
  variant?: 'primary' | 'secondary' | 'accent' | 'ghost' | 'link' | 'outline' | 'error'
  customClasses?: string
  size?: 'xs' | 'sm' | 'md' | 'lg'
  loading?: boolean
  disabled?: boolean
  fullWidth?: boolean
  rounded?: boolean
  borderless?: boolean
  withBorder?: boolean
  type?: 'button' | 'submit' | 'reset'
  /** Playwright / a11y: forwarded as `data-testid` on the native button. */
  dataTestId?: string
  /** E2E: forwarded as `data-gh-id` on the native button. */
  dataGhId?: string
}

const props = withDefaults(defineProps<Props>(), {
  variant: 'primary',
  size: 'md',
  loading: false,
  disabled: false,
  fullWidth: false,
  rounded: false,
  borderless: false,
  withBorder: false,
  type: 'button',
})

const emit = defineEmits<{
  click: [event: MouseEvent]
}>()

const buttonClasses = computed(() => {
  const classes = ['btn', 'shadow-none']

  // Variant
  classes.push(`btn-${props.variant}`)

  // Size
  if (props.size !== 'md') {
    classes.push(`btn-${props.size}`)
  }

  // Full width
  if (props.fullWidth) {
    classes.push('w-full')
  }

  if (props.rounded) {
    classes.push('rounded-full border-[var(--color-neutral-shade-50)]')
  }

  if (props.borderless) {
    classes.push('border-none')
  }

  if (props.withBorder) {
    classes.push('border-[var(--color-neutral-shade-50)]')
  }

  if (props.customClasses) {
    classes.push(props.customClasses)
  }

  return classes.join(' ')
})

const handleClick = (event: MouseEvent) => {
  if (!props.disabled && !props.loading) {
    emit('click', event)
  }
}
</script>

<template>
  <button
    :class="buttonClasses"
    :data-testid="props.dataTestId"
    :data-gh-id="props.dataGhId"
    :disabled="disabled || loading"
    :type="type"
    @click="handleClick"
  >
    <span v-if="loading" class="loading loading-spinner loading-sm"></span>
    <slot />
  </button>
</template>

<style>
.btn-accent {
  --btn-color: var(--color-accent);
  --btn-fg: var(--color-accent-foreground);
}
</style>
